/**
 * <s-product-gallery> — one main image at a time with a thumbnail strip below.
 *
 * Markup:
 *   [data-gallery-track]          horizontal scroll-snap viewport (swipe on touch)
 *   [data-gallery-slide]          one per media; data-media-id="<media id>"
 *   [data-gallery-thumb]          thumbnail buttons, same order as slides
 *   [data-gallery-prev|next]      optional arrow buttons
 *
 * Variant sync: when the product form partial re-renders, an element
 * [data-variant-media-id] inside the matching s-product-form carries the selected
 * variant's featured media id and the gallery moves to that slide.
 */
class SProductGallery extends HTMLElement {
  #ac = null;
  #io = null;
  #mo = null;
  #index = 0;
  #lastMediaId = '';

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    if (!this.track || this.slides.length === 0) return;

    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.go(i), { signal });
    });
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.go(this.#index - 1), { signal });
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.go(this.#index + 1), { signal });

    this.#io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#setActive(this.slides.indexOf(entry.target));
        }
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((slide) => this.#io.observe(slide));

    const form = document.querySelector(`s-product-form[data-product-id="${this.dataset.productId}"]`);
    if (form && !this.closest('dialog')) {
      this.#lastMediaId = form.querySelector('[data-variant-media-id]')?.dataset.variantMediaId || '';
      this.#mo = new MutationObserver(() => this.#syncVariant(form));
      this.#mo.observe(form, { childList: true, subtree: true });
    }

    this.#setActive(0);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#io?.disconnect();
    this.#mo?.disconnect();
  }

  go(i) {
    const count = this.slides.length;
    const index = (i + count) % count;
    const slide = this.slides[index];
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rtl = getComputedStyle(this.track).direction === 'rtl';
    const left = rtl
      ? slide.offsetLeft + slide.offsetWidth - this.track.clientWidth
      : slide.offsetLeft;
    this.track.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
    this.#setActive(index);
  }

  #setActive(index) {
    if (index < 0) return;
    this.#index = index;
    this.thumbs.forEach((thumb, j) => {
      const active = j === index;
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      if (active) {
        const strip = thumb.parentElement?.closest('[data-gallery-thumbs]');
        if (strip) {
          const target = thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
          strip.scrollTo({ left: target, behavior: 'auto' });
        }
      }
    });
  }

  #syncVariant(form) {
    const id = form.querySelector('[data-variant-media-id]')?.dataset.variantMediaId || '';
    if (!id || id === this.#lastMediaId) return;
    this.#lastMediaId = id;
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === id);
    if (index >= 0) this.go(index);
  }
}

/**
 * <s-sticky-atc> — mobile add-to-cart bar shown once the main add-to-cart
 * button ([data-main-atc]) scrolls out of view. Hidden inside dialogs (quick view).
 */
class SStickyAtc extends HTMLElement {
  #io = null;

  connectedCallback() {
    if (this.closest('dialog')) {
      this.hidden = true;
      return;
    }
    const target = document.querySelector('[data-main-atc]');
    if (!target) return;
    this.#io = new IntersectionObserver(([entry]) => {
      const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.toggleAttribute('data-visible', past);
    });
    this.#io.observe(target);
  }

  disconnectedCallback() {
    this.#io?.disconnect();
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
if (!customElements.get('s-sticky-atc')) customElements.define('s-sticky-atc', SStickyAtc);
